import React from 'react';

/**
 * The term a cell should pick out of its own text, or `''` for none.
 *
 * Context rather than a prop threaded to every cell: it is one value for the
 * whole grid and changes rarely, where the cells it reaches are re-rendered
 * constantly for reasons of their own.
 */
export const DataGridHighlightContext = React.createContext('');

/** How many matches one cell will mark before it stops looking. */
const MAX_MATCHES_PER_CELL = 20;

export type HighlightSegment = { text: string; match: boolean };

/**
 * Splits text into the parts that match the term and the parts that do not.
 *
 * `indexOf` on lowered copies rather than a regular expression, for two
 * reasons. A term is whatever somebody typed, so a regex would have to escape
 * it and escaping is the thing that goes wrong; and this has to agree with the
 * SQL that selected the row, which is a plain case-insensitive substring test
 * and nothing cleverer.
 *
 * Returns a single unmatched segment when there is nothing to mark, so the
 * caller has one shape to render either way.
 */
export const splitSearchMatches = (
  text: string,
  term: string
): HighlightSegment[] => {
  const needle = term.trim().toLowerCase();
  if (needle === '' || text === '') {
    return [{ text, match: false }];
  }
  const haystack = text.toLowerCase();
  const segments: HighlightSegment[] = [];
  let cursor = 0;
  let found = 0;
  for (;;) {
    const index =
      found >= MAX_MATCHES_PER_CELL ? -1 : haystack.indexOf(needle, cursor);
    if (index === -1) {
      break;
    }
    if (index > cursor) {
      segments.push({ text: text.slice(cursor, index), match: false });
    }
    segments.push({
      text: text.slice(index, index + needle.length),
      match: true,
    });
    cursor = index + needle.length;
    found += 1;
  }
  if (segments.length === 0) {
    return [{ text, match: false }];
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), match: false });
  }
  return segments;
};

/**
 * The matched parts of a cell's text, marked.
 *
 * Neutral on purpose. Every warm hue is already spoken for inside this grid —
 * amber is a run in progress, emerald a run that succeeded, rose one that
 * failed, and primary is selection and focus — so a coloured highlight would
 * read as a state the row is in rather than as an answer to what was typed.
 *
 * Background only, and the padding is cancelled by an equal negative margin, so
 * marking a cell cannot change where its text sits or where it truncates. A
 * search that nudged every matched row half a character sideways would be a
 * worse kind of noise than the one it is trying to remove.
 *
 * `<mark>` because that is what the element is for, with the browser's own
 * yellow overridden.
 */
export const HighlightedText: React.FC<{ text: string }> = ({ text }) => {
  const term = React.useContext(DataGridHighlightContext);
  const segments = React.useMemo(
    () => splitSearchMatches(text, term),
    [term, text]
  );
  if (segments.length === 1) {
    return <>{text}</>;
  }
  return (
    <>
      {segments.map((segment, index) =>
        segment.match ? (
          <mark
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className="-mx-px rounded-[3px] bg-foreground/15 px-px text-inherit"
          >
            {segment.text}
          </mark>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={index}>{segment.text}</React.Fragment>
        )
      )}
    </>
  );
};
